/* eslint-disable @typescript-eslint/no-floating-promises */
import { Menu, MenuItem } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { saveAs } from "file-saver";
import React from "react";
import { useTranslation } from "react-i18next";
import { ReturnNodeDto, useFileApi } from "~/api/GeneratedApi";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { FileShareDialog } from "./FileShareDialog";
import MoveNodePopupForm from "./MoveNodePopupForm";
import RenameNodePopupForm from "./RenameNodePopupForm";

interface Props {
  currentContextMenuNode: ReturnNodeDto | undefined;
  contextMenuPosition: { mouseX: number | null; mouseY: number | null };
  handleClose: () => void;
  refetch?: () => void;
}

const FileExplorerFileContextMenu = ({
  currentContextMenuNode,
  contextMenuPosition,
  handleClose,
  refetch,
}: Props): JSX.Element => {
  const { t } = useTranslation("file-explorer");
  const { confirm, show } = usePopup();
  const [shareDialogNodeId, setShareDialogNodeId] = React.useState<
    number | null
  >(null);

  const fileApi = useFileApi();

  const { mutate: deleteFile } = useCustomMutation(
    (fileId: number) => fileApi.deleteItem(fileId, true),
    {
      onSuccess: () => {
        handleClose();
        refetch?.();
      },
    },
  );

  const disableMutations =
    currentContextMenuNode?.programManaged ||
    currentContextMenuNode?.reserved ||
    currentContextMenuNode?.parentReserved;

  return (
    <>
      <Menu
        keepMounted
        open={contextMenuPosition.mouseY !== null}
        onContextMenu={(evt) => {
          evt.preventDefault();
          handleClose();
        }}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition.mouseY !== null &&
          contextMenuPosition.mouseX !== null
            ? {
                top: contextMenuPosition.mouseY,
                left: contextMenuPosition.mouseX,
              }
            : undefined
        }
      >
        <span>
          <MenuItem
            dense
            disabled={disableMutations ?? true}
            onClick={() => {
              handleClose();
              if (!currentContextMenuNode) return;
              confirm({
                title: t("general:are-you-sure"),
                description: t(
                  `file-explorer:${
                    currentContextMenuNode.isFolder
                      ? "removeFolder"
                      : "removeFile"
                  }`,
                  {
                    object: currentContextMenuNode.fullName,
                  },
                ),
              }).then(() => deleteFile(currentContextMenuNode?.id));
            }}
          >
            {t("delete")}
          </MenuItem>
          <MenuItem
            dense
            disabled={disableMutations ?? true}
            onClick={() => {
              handleClose();
              if (!currentContextMenuNode) return;
              show(
                <MoveNodePopupForm
                  sourceNode={currentContextMenuNode}
                  onSubmitted={() => refetch?.()}
                />,
                { noDialog: true },
              );
            }}
          >
            {t("move")}
          </MenuItem>
          <MenuItem
            dense
            disabled={disableMutations ?? true}
            onClick={() => {
              handleClose();
              if (!currentContextMenuNode) return;
              show(
                <RenameNodePopupForm
                  node={currentContextMenuNode}
                  onSubmitted={() => refetch?.()}
                />,
                { noDialog: true },
              );
            }}
          >
            {t("rename")}
          </MenuItem>
          <MenuItem
            dense
            onClick={() => {
              handleClose();
              if (!currentContextMenuNode) return;
              fileApi.getFile(currentContextMenuNode.id).then((x) => {
                saveAs(x.data, currentContextMenuNode.fullName);
              });
            }}
          >
            {t("download")}
          </MenuItem>
          <MenuItem
            dense
            disabled={currentContextMenuNode?.isFolder}
            onClick={() => {
              if (!currentContextMenuNode) return;
              setShareDialogNodeId(currentContextMenuNode.id);
            }}
          >
            {t("share")}
          </MenuItem>
        </span>
      </Menu>
      {shareDialogNodeId !== null ? (
        <FileShareDialog
          open={shareDialogNodeId !== null}
          nodeId={shareDialogNodeId}
          onClose={() => setShareDialogNodeId(null)}
        />
      ) : null}
    </>
  );
};

export default FileExplorerFileContextMenu;
